// © Microsoft Corporation. All rights reserved.

import { CommonHeaderConfig, CommonHeaderLocalizedStrings, NavigationItem } from "./CommonHeader.config";
import { CommonHeaderProps, ContextualNavItem, HamburgerMenuData, HamburgerMenuItem, SearchProps, SearchTelemetryContext, SettingsProps } from "./CommonHeader.props";
import { IRequestContext, InspectorArgs, InspectorResponse, getAppEnvironment } from "@msnews/core";

import { AppError } from "@msnews/diagnostics";
import { CommonHeaderIconMap } from "./CommonHeader.icons";
import { CommonHeaderState } from "./CommonHeader.state";
import { FeedNavigationData } from "@msnews/feed-navigation";
import { MeControlComponentProps } from "@msnews/me-control-component";
import { SearchFormParameters } from "@msnews/search-box";
import { TelemetryObject } from "@msnews/telemetry-core";

export interface CommonHeaderConnectorArgs {
    /** Callback for handling a navigation item content rendering */
    handleRenderInfo?: (event: CustomEvent) => void;

    /** Callback to navigate to "My Feed" */
    goToMyFeed?: () => void;

    /** Callback to navigate to "Personalize" */
    goToPersonalize?: () => void;

    /** Function to update the form parameters */
    updateFormParameters?: () => Array<SearchFormParameters>;

    /** Function to send telemetry action eventprops */
    sendTelemetryActionEvent?: (element?, action?, behavior?, event?, destination?, headline?) => void;

    /** Function to map contextual nav telemetry props */
    mapContextualNavTelemetryProps: (navItem: NavigationItem) => TelemetryObject;

    /** Function to map hamburger menu telemetry props */
    mapHamburgerMenuTelemetryProps: (navItem: NavigationItem) => TelemetryObject;

    /** Function to get the form code to use for the common search box */
    getFormCode?: (formParameters: Array<SearchFormParameters>) => string;

    /** Request id */
    requestId: string;

    /** Request context */
    requestContext: IRequestContext;

    /** Handler to log app errors.  */
    appErrorCallback?: (error: AppError) => void;

    /** Callback for language selection update */
    languageUpdatedCallback?: (market: string) => void;

    /** Telemetry data for search box */
    searchTelemetryContext: SearchTelemetryContext;

    /** boolean for secondary nav */
    isContextualSecondaryNavEnabled: boolean;
}

export const CommonHeaderInspector: any = {
    inspect: (args: InspectorArgs<CommonHeaderConfig, CommonHeaderState, CommonHeaderConnectorArgs>): InspectorResponse<CommonHeaderProps> => {

        const { config, state, connectorArgs } = args;
        const { lightLogo, darkLogo, localizedStrings } = config;

        // this is to improve perf for common header load for ttvr.
        const isLoadingNavAndMenu = !config.isDelayLoadNavAndMenuEnabled  ||
            (config.isDelayLoadNavAndMenuEnabled && state.isTtvrFired);

        const props: CommonHeaderProps = {
            hamburgerMenuProps: getHamburgerMenuProps(config, state, connectorArgs, isLoadingNavAndMenu),
            contextualNavProps: getContextualNavProps(config, connectorArgs, isLoadingNavAndMenu),
            contextualSecondaryNavProps: getContextualSecondaryNavProps(config, connectorArgs, isLoadingNavAndMenu),
            isContextualSecondaryNavEnabled: connectorArgs.isContextualSecondaryNavEnabled,
            handleRenderInfo: connectorArgs.handleRenderInfo,
            selectedNavItem: state.selectedNavItem,
            selectedNavMenuItem: state.selectedNavMenuItem,
            selectedSecondaryNavItem: state.selectedSecondaryNavItem,
            selectedSecondaryNavMenuItem: state.selectedSecondaryNavMenuItem,
            searchProps: getSearchProps(config, state, connectorArgs),
            meControlProps: getMeControlProps(config, connectorArgs),
            goToMyFeed: connectorArgs.goToMyFeed,
            disableFirstRow: config.disableFirstRow,
            appErrorCallback: connectorArgs.appErrorCallback,
            settingsProps: getSettingsProps(config, connectorArgs),
            lightLogo,
            darkLogo,
            logoAltText: localizedStrings && localizedStrings.logoAltText
        };

        return {
            props: props,
            missingChildren: []
        };
    }
};

/**
 * Gets the hamburger menu props from config and state
 * @param config navigation config
 * @param state common header state
 * @param state common header state
 * @param connectorArgs common header state
 * @param isResolvingHamburgerMenuItems flag to decide whether to reoslve hamger menu items
 */
function getHamburgerMenuProps(
    config: CommonHeaderConfig,
    state: Partial<CommonHeaderState>,
    connectorArgs: CommonHeaderConnectorArgs,
    isResolvingHamburgerMenuItems: boolean): HamburgerMenuData<HamburgerMenuItem> {
    const { navigationConfig, localizedStrings } = config;
    if (!navigationConfig.hamburgerMenuItems.length) {
        return null;
    }

    // Convert the items from the config to hamburger menu items
    const hamburgerMenuItems = !isResolvingHamburgerMenuItems ? null : navigationConfig.hamburgerMenuItems.map(item => convertNavigationItemToHamburgerMenuItem(item, connectorArgs, localizedStrings));

    return {
        menuButtonTooltip: localizedStrings && localizedStrings.menuButtonTooltip,
        closeButtonTooltip: localizedStrings && localizedStrings.closeButtonTooltip,
        navItems: hamburgerMenuItems,
        selectedNavId: state.selectedNavItem && state.selectedNavItem.id
    };
}

/**
 * Convert navigation items to menu item props
 * @param navigationItem navigation item
 */
function convertNavigationItemToHamburgerMenuItem(item: NavigationItem, connectorArgs: CommonHeaderConnectorArgs, localizedStrings: CommonHeaderLocalizedStrings): HamburgerMenuItem {
    if (!item) {
        return null;
    }

    let childItems = null;
    if (item.children && item.children.length) {
        childItems = item.children.map(child => convertNavigationItemToHamburgerMenuItem(child, connectorArgs, localizedStrings));
    }

    return {
        ...item,
        display: item.displayKey ? localizedStrings[item.displayKey] : item.display,
        glyph: CommonHeaderIconMap[item.iconName],
        childItems: childItems,
        telemetryMetadata: connectorArgs.mapHamburgerMenuTelemetryProps(item)
    };
}

/**
 * Gets the contextual nav props from config
 * @param config common header config info
 * @param connectorArgs connector arguments
 * @param isResolvingFeedsMenuItems flag to determine resolving feeds menu items
 */
function getContextualNavProps(
    config: CommonHeaderConfig,
    connectorArgs: CommonHeaderConnectorArgs,
    isResolvingFeedsMenuItems: boolean
): FeedNavigationData<ContextualNavItem> {
    const { navigationConfig, localizedStrings } = config || {};

    if (!navigationConfig.contextualNavItems.length) {
        return null;
    }

    return {
        navGroups: {
            home: convertNavigationItemToFeedNavigationItem(navigationConfig.homeNavigationItem, connectorArgs, localizedStrings),
            feeds: isResolvingFeedsMenuItems ?
                navigationConfig.contextualNavItems.map(item => convertNavigationItemToFeedNavigationItem(item, connectorArgs, localizedStrings))
                : []
        },
        localizedStrings: {
            overflowButtonTooltip: localizedStrings.overflowButtonTooltip,
            personalizeButtonText: localizedStrings.personalizeButtonText
        },
        selectedNavItem: null,
        selectedNavMenuItem: null,
        interestsNavData: convertNavigationItemToFeedNavigationItem(navigationConfig.interestsNavigationItem, connectorArgs, localizedStrings)
    };
}
/**
 * Gets the contextual secondary nav props from config
 * @param config common header config
 * @param connectorArgs connector arguments
 * @param isResolvingFeedsMenuItems flag to determine resolving feeds menu items
 */
function getContextualSecondaryNavProps(
    config: CommonHeaderConfig,
    connectorArgs: CommonHeaderConnectorArgs,
    isResolvingFeedsMenuItems: boolean
): FeedNavigationData<ContextualNavItem> {
    const { navigationConfig, localizedStrings } = config || {};

    if (!navigationConfig.contextualNavItems.length) {
        return null;
    }

    return {
        navGroups: {
            home: convertNavigationItemToFeedNavigationItem(navigationConfig.secondaryNavigationItem, connectorArgs, localizedStrings),
            feeds: isResolvingFeedsMenuItems ?
                navigationConfig.contextualNavItems.map(item => convertNavigationItemToFeedNavigationItem(item, connectorArgs, localizedStrings))
                : []
        },
        localizedStrings: {
            overflowButtonTooltip: localizedStrings.overflowButtonTooltip,
            personalizeButtonText: localizedStrings.personalizeButtonText
        },
        selectedNavItem: null,
        selectedNavMenuItem: null,
        interestsNavData: null
    };
}

/**
 * Convert navigation item to feed navigation item
 * @param item navigation item
 */
function convertNavigationItemToFeedNavigationItem(item: NavigationItem, connectorArgs: CommonHeaderConnectorArgs, localizedStrings: CommonHeaderLocalizedStrings): ContextualNavItem {
    if (!item) {
        return null;
    }

    const { renderInfo, children = [] } = item;

    // TODO: Move path property into NavigationItem object instead of RenderInfo
    return {
        ...item,
        display: item.displayKey ? localizedStrings[item.displayKey] : item.display,
        path: renderInfo && renderInfo.path,
        children: children.length ? children.map(child => convertNavigationItemToFeedNavigationItem(child, connectorArgs, localizedStrings)) : [],
        telemetryMetadata: connectorArgs.mapContextualNavTelemetryProps(item)
    };
}

/**
 * Gets props for the search box
 * @param config common header config
 * @param state common header state
 * @param connectorArgs common header connector args
 */
function getSearchProps(config: CommonHeaderConfig, state: Partial<CommonHeaderState>, connectorArgs: CommonHeaderConnectorArgs): SearchProps {
    const { searchBoxConfig, localizedStrings } = config;
    const { getFormCode, requestId, searchTelemetryContext, sendTelemetryActionEvent, updateFormParameters } = connectorArgs;

    const formParameters = updateFormParameters && updateFormParameters();

    const searchProps: SearchProps = {
        // We set the id in the common header component
        id: undefined,
        action: searchBoxConfig.searchActionUrl,
        autoFocus: searchBoxConfig.enableSearchFormFocus,
        autoSuggest: searchBoxConfig.autoSuggest,
        "data-form-code": getFormCode && getFormCode(formParameters),
        formParameters,
        isOnImage: true,
        keyPressCaptureEnabled: searchBoxConfig.enableKeyPressCapture,
        maxLength: 250,
        placeholder: localizedStrings && localizedStrings.searchPlaceholderText,
        requestId,
        target: searchBoxConfig.target,
        title: localizedStrings && localizedStrings.searchTooltip,
        buttonLabel: localizedStrings && localizedStrings.searchButtonTooltip,
        telemetryContext: searchTelemetryContext || {},
        telemetryCallback: sendTelemetryActionEvent
    };

    if (searchProps.telemetryContext.buttonObject && searchProps.telemetryContext.buttonObject.contract) {
        searchTelemetryContext.buttonObject.contract.destinationUrl = searchProps.action;
    }

    return searchProps;
}

/**
 * Gets props for the me control
 * @param config common header config
 */
function getMeControlProps(config: CommonHeaderConfig, connectorArgs: CommonHeaderConnectorArgs): MeControlComponentProps {
    const { meControlConfig, localizedStrings } = config;
    const displayMode = "Compressed";

    return {
        partnerId: meControlConfig.partnerId,
        signInTooltipText: localizedStrings && localizedStrings.signInTooltip,
        signedInNarrowPageDisplayMode: displayMode,
        signedOutNarrowPageDisplayMode: displayMode,
        signedInWidePageDisplayMode: displayMode,
        signedOutWidePageDisplayMode: displayMode,
        requestContext: connectorArgs.requestContext
    };
}

/**
 * Gets props for the settings dialog
 * @param config common header config
 * @param connectorArgs common header connector args
 */
function getSettingsProps(config: CommonHeaderConfig, connectorArgs: CommonHeaderConnectorArgs): SettingsProps {
    const { localizedStrings } = config;

    return {
        goToPersonalize: connectorArgs.goToPersonalize,
        localizedStrings: {
            settingsDialogTitle: localizedStrings && localizedStrings.settingsDialogTitle,
            languageDropdownLabel: localizedStrings && localizedStrings.languageDropdownLabel,
            settingsDialogPersonalizeLabel: localizedStrings && localizedStrings.settingsDialogPersonalizeLabel,
            settingsDialogPersonalizeButtonText: localizedStrings && localizedStrings.settingsDialogPersonalizeButtonText,
            settingsButtonTooltip: localizedStrings && localizedStrings.settingsButtonTooltip,
            settingsCloseButtonTooltip: localizedStrings && localizedStrings.settingsCloseButtonTooltip
        },
        languageUpdatedCallback: connectorArgs.languageUpdatedCallback,
        hideMarketSelector: getAppEnvironment().IsCnDomain
    };
}
